/**
 * BrowseTab.jsx — Open House Planner browse tab (/openhouse?tab=browse)
 *
 * Weekly workflow: scrape lands (Redfin + Village Properties) → filter by
 * day/area/brokerage/price → multi-select → add to a day plan (draft oh_plan
 * + oh_plan_stops). Also: AI paste-import, manual add, per-card archive,
 * lazy geocoding of rows missing coords so pins appear on the shared map.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Stack, Typography, Button, IconButton, Chip, TextField, InputAdornment,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Radio, RadioGroup,
  FormControlLabel, FormControl, InputLabel, Select, MenuItem, Autocomplete,
  CircularProgress, Paper, Menu, Tooltip, Collapse, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Archive as ArchiveIcon,
  HomeWork as HouseIcon,
  Map as MapIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AiIcon,
  PlaylistAdd as PlaylistAddIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  useOpenHouses, useCreateOpenHouse, useUpdateOpenHouse, useRefreshOpenHouses,
  useParseOpenHousePaste, useOhPlans, useCreateOhPlan, useCreateOhPlanStop,
  useAllOhPlanStops, useEnrichZillow, useZillowStatus,
} from '../../hooks/useOpenHouses';
import OpenHouseDetailModal from './OpenHouseDetailModal';
import { useAuth } from '../../hooks/useAuth';
import { useMobile } from '../../hooks/useMobile';
import { useFocus } from '../../hooks/useFocus';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';
import { fmtD } from '../../lib/utils';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import OpenHouseMap from './OpenHouseMap';
import { geocodeAddress, toPtIso, DEFAULT_PLAN_START } from './geo';

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_KEY;
const LIBRARIES = ['places'];

const PT = 'America/Los_Angeles';
const SOUTH_COUNTY = ['santa barbara', 'montecito', 'goleta', 'carpinteria', 'summerland'];
const VILLAGE_COLOR = '#E879F9';
const SOURCE_LABEL = { redfin: 'Redfin', village: 'Village', ai_import: 'AI', manual: 'Manual' };

// ── PT date/time helpers ────────────────────────────────────────────────

/** ISO timestamp → 'YYYY-MM-DD' in Pacific time. */
function ptDateKey(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PT, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(iso));
}

function ptTodayKey() {
  return ptDateKey(new Date().toISOString());
}

/** 'YYYY-MM-DD' → 'Sat Jul 5' (date-only, no TZ math needed). */
function ptDayLabel(dateKey) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T12:00:00`);
  if (isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
}

/** ISO → '1 PM' / '1:30 PM' in Pacific time. */
function fmtPtTime(iso) {
  return new Date(iso)
    .toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
}

/** OH window: 'Sat 1–4 PM' in Pacific time. */
function fmtWindow(startIso, endIso) {
  if (!startIso) return '';
  const day = new Date(startIso).toLocaleDateString('en-US', { timeZone: PT, weekday: 'short' });
  const s = fmtPtTime(startIso);
  if (!endIso) return `${day} ${s}`;
  const e = fmtPtTime(endIso);
  const sameSuffix = s.slice(-2) === e.slice(-2);
  return `${day} ${sameSuffix ? s.slice(0, -3) : s}–${e}`;
}

/** Compact price: '$2.4M', '$950K'. */
function fmtPrice(p) {
  const n = parseFloat(p);
  if (!n || isNaN(n)) return '';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

/** Short pin label: '2.4M', '950K' (no $ to keep pins compact). */
function pinLabel(price) {
  const n = parseFloat(price);
  if (!n || isNaN(n)) return '';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  return `${Math.round(n / 1e3)}K`;
}

function isVillage(brokerage) {
  return (brokerage || '').toLowerCase().includes('village');
}

/** '13:30' (input type=time) → '1:30 PM' for toPtIso. */
function time24To12(t) {
  const [h, m] = String(t || '').split(':').map(Number);
  if (isNaN(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${period}`;
}

function agoLabel(iso) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** Most frequent non-empty value in an array. */
function modeOf(arr) {
  const counts = {};
  let best = null;
  let bestN = 0;
  for (const v of arr) {
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > bestN) { best = v; bestN = counts[v]; }
  }
  return best;
}

function refreshMessage(res) {
  const redfin = res?.redfin ?? res?.redfinCount ?? res?.counts?.redfin ?? 0;
  const village = res?.village ?? res?.villageCount ?? res?.counts?.village ?? 0;
  const matched = res?.matched ?? res?.agentsMatched ?? res?.counts?.matched ?? 0;
  return `Scraped ${redfin} from Redfin, ${village} from Village — ${matched} agents matched`;
}

// ── Add-to-plan dialog ──────────────────────────────────────────────────

function AddToPlanDialog({ open, onClose, houses, dayKey, selectedPlanId, onDone }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { data: plans = [] } = useOhPlans();
  const { data: allStops = [] } = useAllOhPlanStops();
  const createPlan = useCreateOhPlan();
  const createStop = useCreateOhPlanStop();

  const [choice, setChoice] = useState('new');
  const [title, setTitle] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [departTime, setDepartTime] = useState('10:30');
  const [saving, setSaving] = useState(false);

  const draftPlans = useMemo(
    () => plans
      .filter((p) => p.status === 'draft')
      .sort((a, b) => String(b.planDate || '').localeCompare(String(a.planDate || ''))),
    [plans],
  );

  useEffect(() => {
    if (!open) return;
    const date = dayKey || (houses[0]?.startAt ? ptDateKey(houses[0].startAt) : ptTodayKey());
    const dominant = modeOf(houses.map((h) => h.brokerage)) || modeOf(houses.map((h) => h.city)) || 'Open House';
    setChoice(selectedPlanId && draftPlans.some((p) => p.id === selectedPlanId) ? selectedPlanId : 'new');
    setTitle(`${ptDayLabel(date)} — ${dominant} circuit`);
    setPlanDate(date);
    setDepartTime('10:30');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAdd = async () => {
    setSaving(true);
    try {
      let planId = choice;
      let planTitle = title.trim() || 'Open house circuit';
      if (choice === 'new') {
        const plan = await createPlan.mutateAsync({
          title: planTitle,
          planDate,
          departAt: planDate && departTime ? toPtIso(planDate, time24To12(departTime)) : null,
          startAddress: DEFAULT_PLAN_START.address,
          startLat: DEFAULT_PLAN_START.lat,
          startLng: DEFAULT_PLAN_START.lng,
          status: 'draft',
          createdBy: profile?.id || null,
        });
        planId = plan.id;
      } else {
        planTitle = draftPlans.find((p) => p.id === choice)?.title || 'plan';
      }

      const existing = allStops.filter((s) => s.planId === planId);
      const existingIds = new Set(existing.map((s) => s.openHouseId));
      let position = existing.reduce((m, s) => Math.max(m, s.position ?? 0), -1) + 1;

      let added = 0;
      const ordered = [...houses].sort((a, b) => String(a.startAt || '').localeCompare(String(b.startAt || '')));
      for (const oh of ordered) {
        if (existingIds.has(oh.id)) continue;
        await createStop.mutateAsync({
          planId,
          openHouseId: oh.id,
          position: position++,
          status: 'pending',
        });
        added += 1;
      }

      logAction({
        action: 'Create OH Plan',
        user: profile?.full_name || profile?.email,
        description: `Added ${added} open house${added === 1 ? '' : 's'} to "${planTitle}"`,
        details: { planId, added, skipped: houses.length - added, newPlan: choice === 'new' },
      });
      toast(`Added ${added} stop${added === 1 ? '' : 's'} to "${planTitle}"`, 'success');
      onDone?.(planId);
    } catch (e) {
      toast(`Failed to add to plan: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add {houses.length} to plan</DialogTitle>
      <DialogContent>
        <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
          {draftPlans.map((p) => (
            <FormControlLabel
              key={p.id}
              value={p.id}
              control={<Radio size="small" />}
              label={`${p.title || 'Untitled plan'} — ${fmtD(p.planDate)}`}
            />
          ))}
          <FormControlLabel value="new" control={<Radio size="small" />} label="New plan" />
        </RadioGroup>
        <Collapse in={choice === 'new'}>
          <Stack spacing={1.5} sx={{ mt: 1, pl: 3.5 }}>
            <TextField
              size="small"
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                type="date"
                label="Date"
                value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="time"
                label="Depart"
                value={departTime}
                onChange={(e) => setDepartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={saving || houses.length === 0 || (choice === 'new' && !planDate)}
          startIcon={saving ? <CircularProgress size={14} /> : <PlaylistAddIcon />}
        >
          Add to plan
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AI paste-import dialog ──────────────────────────────────────────────

function ImportPasteDialog({ open, onClose }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const parseMut = useParseOpenHousePaste();
  const createOh = useCreateOpenHouse();

  const [text, setText] = useState('');
  const [entries, setEntries] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setText(''); setEntries(null); setSaving(false); }
  }, [open]);

  const handleParse = () => {
    parseMut.mutate(text, {
      onSuccess: (res) => {
        const rows = (res?.entries || []).filter((r) => r.address);
        if (!rows.length) {
          toast('No open houses found in that text', 'info');
          return;
        }
        setEntries(rows);
      },
      onError: (e) => toast(`Parse failed: ${e.message}`, 'error'),
    });
  };

  const updateEntry = (i, key, val) =>
    setEntries((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const removeEntry = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const handleAdd = async () => {
    setSaving(true);
    let created = 0;
    for (const e of entries) {
      if (!e.address) continue;
      try {
        let startAt = null;
        let endAt = null;
        try { if (e.date && e.startTime) startAt = toPtIso(e.date, e.startTime); } catch { /* keep null */ }
        try { if (e.date && e.endTime) endAt = toPtIso(e.date, e.endTime); } catch { /* keep null */ }
        let coords = { lat: null, lng: null };
        try {
          coords = await geocodeAddress([e.address, e.city, 'CA'].filter(Boolean).join(', '));
        } catch { /* tolerate — pin appears after lazy geocode or never */ }
        await createOh.mutateAsync({
          address: e.address,
          city: e.city || null,
          zip: e.zip || null,
          price: e.price || null,
          beds: e.beds || null,
          baths: e.baths || null,
          sqft: e.sqft || null,
          startAt,
          endAt,
          agentName: e.agentName || null,
          agentPhone: e.agentPhone || null,
          brokerage: e.brokerage || null,
          listingUrl: e.listingUrl || null,
          lat: coords.lat,
          lng: coords.lng,
          source: 'ai_import',
          status: 'active',
        });
        created += 1;
      } catch (err) {
        console.warn('[OH import] row failed:', err.message);
      }
    }
    setSaving(false);
    if (created) {
      logAction({
        action: 'Import Open Houses',
        user: profile?.full_name || profile?.email,
        description: `AI paste-import added ${created} open house${created === 1 ? '' : 's'}`,
        details: { created, parsed: entries.length },
      });
      toast(`Added ${created} open house${created === 1 ? '' : 's'}`, 'success');
      onClose();
    } else {
      toast('Nothing was added — all rows failed', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>Import open houses (AI paste)</DialogTitle>
      <DialogContent>
        {!entries ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Paste anything — a Zillow/Redfin page, a brokerage email, an MLS blast. AI pulls out the open houses.
            </Typography>
            <TextField
              multiline
              rows={10}
              fullWidth
              autoFocus
              placeholder="Paste listing text here…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell sx={{ width: 120 }}>Date</TableCell>
                  <TableCell sx={{ width: 90 }}>Start</TableCell>
                  <TableCell sx={{ width: 90 }}>End</TableCell>
                  <TableCell sx={{ width: 140 }}>Agent</TableCell>
                  <TableCell sx={{ width: 150 }}>Brokerage</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.address || ''} onChange={(e) => updateEntry(i, 'address', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.date || ''} onChange={(e) => updateEntry(i, 'date', e.target.value)} placeholder="YYYY-MM-DD" />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.startTime || ''} onChange={(e) => updateEntry(i, 'startTime', e.target.value)} placeholder="1:00 PM" />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.endTime || ''} onChange={(e) => updateEntry(i, 'endTime', e.target.value)} placeholder="4:00 PM" />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.agentName || ''} onChange={(e) => updateEntry(i, 'agentName', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField variant="standard" size="small" fullWidth value={row.brokerage || ''} onChange={(e) => updateEntry(i, 'brokerage', e.target.value)} />
                    </TableCell>
                    <TableCell padding="none">
                      <IconButton size="small" onClick={() => removeEntry(i)} aria-label="Remove row">
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {!entries ? (
          <Button
            variant="contained"
            onClick={handleParse}
            disabled={parseMut.isPending || text.trim().length < 10}
            startIcon={parseMut.isPending ? <CircularProgress size={14} /> : <AiIcon />}
          >
            Parse
          </Button>
        ) : (
          <>
            <Button onClick={() => setEntries(null)} disabled={saving}>Back</Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={saving || entries.length === 0}
              startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
            >
              Add {entries.length}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Manual add dialog ───────────────────────────────────────────────────

function ManualAddDialog({ open, onClose }) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const createOh = useCreateOpenHouse();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ address: '', date: ptTodayKey(), start: '13:00', end: '16:00', price: '', agentName: '', brokerage: '' });
      setSaving(false);
    }
  }, [open]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.address?.trim()) { toast('Address is required', 'error'); return; }
    setSaving(true);
    try {
      let coords = { lat: null, lng: null };
      try { coords = await geocodeAddress(form.address); } catch { /* tolerate */ }
      const city = form.address.split(',')[1]?.trim() || null;
      await createOh.mutateAsync({
        address: form.address.trim(),
        city,
        price: form.price ? parseFloat(form.price) : null,
        startAt: form.date && form.start ? toPtIso(form.date, time24To12(form.start)) : null,
        endAt: form.date && form.end ? toPtIso(form.date, time24To12(form.end)) : null,
        agentName: form.agentName?.trim() || null,
        brokerage: form.brokerage?.trim() || null,
        lat: coords.lat,
        lng: coords.lng,
        source: 'manual',
        status: 'active',
      });
      logAction({
        action: 'Add Open House',
        user: profile?.full_name || profile?.email,
        description: `Manually added open house at ${form.address.trim()}`,
        details: { address: form.address.trim() },
      });
      toast('Open house added', 'success');
      onClose();
    } catch (e) {
      toast(`Failed to add: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add open house</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <AddressAutocomplete
            variant="mui"
            label="Address"
            placeholder="Street address"
            value={form.address || ''}
            onChange={set('address')}
          />
          <TextField
            size="small"
            type="date"
            label="Date"
            value={form.date || ''}
            onChange={(e) => set('date')(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="time"
              label="Start"
              value={form.start || ''}
              onChange={(e) => set('start')(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="time"
              label="End"
              value={form.end || ''}
              onChange={(e) => set('end')(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            size="small"
            type="number"
            label="Price"
            value={form.price || ''}
            onChange={(e) => set('price')(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            fullWidth
          />
          <TextField
            size="small"
            label="Agent"
            value={form.agentName || ''}
            onChange={(e) => set('agentName')(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Brokerage"
            value={form.brokerage || ''}
            onChange={(e) => set('brokerage')(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── List card ───────────────────────────────────────────────────────────

function HouseCard({ oh, selected, focused, onToggle, onFocus, onArchive, onOpenDetail, cardRef }) {
  const { isHidden } = useFocus();
  const village = isVillage(oh.brokerage);
  const specs = [
    oh.city,
    oh.beds && `${parseFloat(oh.beds)} bd`,
    oh.baths && `${parseFloat(oh.baths)} ba`,
    oh.sqft && `${Number(oh.sqft).toLocaleString()} sf`,
  ].filter(Boolean).join(' · ');

  return (
    <Paper
      ref={cardRef}
      variant="outlined"
      onClick={() => onFocus(oh.id)}
      sx={{
        p: 1.25,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
        cursor: 'pointer',
        borderColor: focused ? 'primary.main' : 'divider',
        boxShadow: focused ? '0 0 0 1px' : 'none',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <Checkbox
        size="small"
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(oh.id)}
        sx={{ p: 0.5, mt: 0.5 }}
      />
      <Box
        onClick={(e) => { e.stopPropagation(); onOpenDetail?.(oh); }}
        sx={{ position: 'relative', width: 88, height: 88, flexShrink: 0, cursor: 'pointer', '&:hover .oh-photo-badge': { opacity: 1 } }}
      >
        {oh.photoUrl ? (
          <Box component="img" src={oh.photoUrl} alt="" sx={{ width: 88, height: 88, borderRadius: 1, objectFit: 'cover' }} />
        ) : (
          <Box sx={{ width: 88, height: 88, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HouseIcon color="disabled" />
          </Box>
        )}
        {(oh.data?.photos?.length > 1) && (
          <Chip size="small" icon={<PhotoLibraryIcon sx={{ fontSize: 12 }} />} label={oh.data.photos.length}
            sx={{ position: 'absolute', bottom: 3, right: 3, height: 18, fontSize: 10, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '& .MuiChip-icon': { color: '#fff', ml: 0.5 } }} />
        )}
        <Box className="oh-photo-badge" sx={{ position: 'absolute', inset: 0, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.3)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 120ms', fontSize: 11, fontWeight: 600 }}>
          View
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>{oh.address}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {specs}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>{fmtPrice(oh.price)}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {fmtWindow(oh.startAt, oh.endAt)}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
          {oh.agentName && (
            <Typography variant="caption" sx={{ fontWeight: 500, mr: 0.5 }} noWrap>{oh.agentName}</Typography>
          )}
          {oh.brokerage && (
            <Chip
              size="small"
              label={oh.brokerage}
              sx={{
                height: 18,
                fontSize: 10,
                maxWidth: 160,
                ...(village ? { bgcolor: VILLAGE_COLOR, color: '#fff', fontWeight: 600 } : {}),
              }}
            />
          )}
          <Chip size="small" variant="outlined" label={SOURCE_LABEL[oh.source] || oh.source} sx={{ height: 18, fontSize: 10 }} />
          {oh.contactId && (
            <Chip size="small" color="success" variant="outlined" label="In CRM" sx={{ height: 18, fontSize: 10 }} />
          )}
          <Box sx={{ flex: 1 }} />
          {oh.listingUrl && (
            <Tooltip title="View listing">
              <IconButton
                size="small"
                component="a"
                href={oh.listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {!isHidden('openhouse.browseMore') && (
            <Tooltip title="Archive">
              <IconButton size="small" data-focus="openhouse.browseMore" onClick={(e) => { e.stopPropagation(); onArchive(oh); }}>
                <ArchiveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

// ── Main tab ────────────────────────────────────────────────────────────

export default function BrowseTab({ selectedPlanId, onOpenPlan }) {
  const { isMobile } = useMobile();
  const { toast } = useToast();
  const { isHidden } = useFocus();
  const { data: openHouses = [], isLoading } = useOpenHouses();
  const refreshMut = useRefreshOpenHouses();
  const updateOh = useUpdateOpenHouse();
  const enrichMut = useEnrichZillow();
  const { data: zillowStatus } = useZillowStatus();
  const [detailOh, setDetailOh] = useState(null);

  const handleEnrichZillow = () => {
    if (zillowStatus && !zillowStatus.configured) {
      toast('Add an Apify token in Settings to enable Zillow photos & agents', 'info');
      return;
    }
    enrichMut.mutate(undefined, {
      onSuccess: (r) => toast(
        r?.skipped ? `Zillow enrichment ${r.reason}` : 'Fetching photos & agents from Zillow — listings update as they come in',
        r?.skipped ? 'info' : 'success',
      ),
      onError: (e) => toast(`Zillow enrichment failed: ${e.message}`, 'error'),
    });
  };
  const zillowRunning = !!zillowStatus?.running || enrichMut.isPending;

  // Load Maps JS even when the map is collapsed (mobile) — geocoding needs it.
  // Options must be identical everywhere useJsApiLoader is called.
  const { isLoaded: mapsReady } = useJsApiLoader({
    id: 'rfos-google-maps',
    googleMapsApiKey: MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  // Filters
  const [dayKey, setDayKey] = useState(null); // null = all days
  const dayDefaulted = useRef(false);
  const [areaMode, setAreaMode] = useState('south');
  const [cities, setCities] = useState([]);
  const [brokerage, setBrokerage] = useState(null);
  const [villageOnly, setVillageOnly] = useState(false);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sqftMin, setSqftMin] = useState('');
  const [sqftMax, setSqftMax] = useState('');
  const [bedsMin, setBedsMin] = useState(0);
  const [sortBy, setSortBy] = useState('time');
  const [search, setSearch] = useState('');

  // Selection / focus / UI
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [focusId, setFocusId] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const cardRefs = useRef({});

  const active = useMemo(() => openHouses.filter((oh) => oh.status === 'active'), [openHouses]);

  const lastScrape = useMemo(() => {
    let max = null;
    for (const oh of openHouses) {
      if (oh.source !== 'redfin' && oh.source !== 'village') continue;
      const t = oh.updatedAt || oh.createdAt;
      if (t && (!max || t > max)) max = t;
    }
    return max;
  }, [openHouses]);

  const scrapedCount = useMemo(
    () => openHouses.filter((oh) => oh.source === 'redfin' || oh.source === 'village').length,
    [openHouses],
  );

  // Day chips from data (PT date of startAt)
  const dayOptions = useMemo(() => {
    const keys = new Set(active.filter((oh) => oh.startAt).map((oh) => ptDateKey(oh.startAt)));
    return [...keys].sort();
  }, [active]);

  // Default-select the next upcoming day that has entries
  useEffect(() => {
    if (dayDefaulted.current || dayOptions.length === 0) return;
    dayDefaulted.current = true;
    const today = ptTodayKey();
    setDayKey(dayOptions.find((k) => k >= today) || dayOptions[dayOptions.length - 1]);
  }, [dayOptions]);

  const cityOptions = useMemo(
    () => [...new Set(active.map((oh) => oh.city).filter(Boolean))].sort(),
    [active],
  );
  // Brokerage → count within the current day + area context (not the brokerage/
  // price/sqft filters, so counts don't go circular). Powers "Name (N)" options.
  const brokerageCounts = useMemo(() => {
    const counts = new Map();
    for (const oh of active) {
      if (!oh.brokerage) continue;
      if (dayKey && (!oh.startAt || ptDateKey(oh.startAt) !== dayKey)) continue;
      const city = (oh.city || '').toLowerCase();
      if (cities.length > 0) {
        if (!cities.some((c) => c.toLowerCase() === city)) continue;
      } else if (areaMode === 'south' && city && !SOUTH_COUNTY.includes(city)) {
        continue;
      }
      counts.set(oh.brokerage, (counts.get(oh.brokerage) || 0) + 1);
    }
    return counts;
  }, [active, dayKey, cities, areaMode]);
  const brokerageOptions = useMemo(
    () => [...brokerageCounts.keys()].sort(
      (a, b) => (brokerageCounts.get(b) - brokerageCounts.get(a)) || a.localeCompare(b),
    ),
    [brokerageCounts],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return active
      .filter((oh) => {
        if (dayKey && (!oh.startAt || ptDateKey(oh.startAt) !== dayKey)) return false;
        const city = (oh.city || '').toLowerCase();
        if (cities.length > 0) {
          if (!cities.some((c) => c.toLowerCase() === city)) return false;
        } else if (areaMode === 'south' && city && !SOUTH_COUNTY.includes(city)) {
          return false;
        }
        if (brokerage && (oh.brokerage || '') !== brokerage) return false;
        if (villageOnly && !isVillage(oh.brokerage)) return false;
        if (minPrice && (!oh.price || parseFloat(oh.price) < minPrice)) return false;
        if (maxPrice && (!oh.price || parseFloat(oh.price) > maxPrice)) return false;
        const sf = oh.sqft ? parseFloat(oh.sqft) : null;
        if (sqftMin !== '' && (sf == null || sf < Number(sqftMin))) return false;
        if (sqftMax !== '' && (sf == null || sf > Number(sqftMax))) return false;
        if (bedsMin && (!oh.beds || parseFloat(oh.beds) < bedsMin)) return false;
        if (q && !`${oh.address || ''} ${oh.agentName || ''} ${oh.brokerage || ''}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        const num = (v) => (v == null || v === '' ? null : parseFloat(v));
        const descNulls = (x, y) => {
          if (x == null && y == null) return 0;
          if (x == null) return 1;
          if (y == null) return -1;
          return y - x;
        };
        switch (sortBy) {
          case 'priceDesc': return descNulls(num(a.price), num(b.price));
          case 'priceAsc': { const r = descNulls(num(a.price), num(b.price)); return r === 0 ? 0 : -r; }
          case 'sqftDesc': return descNulls(num(a.sqft), num(b.sqft));
          case 'sqftAsc': { const r = descNulls(num(a.sqft), num(b.sqft)); return r === 0 ? 0 : -r; }
          case 'bedsDesc': return descNulls(num(a.beds), num(b.beds));
          default: // 'time'
            if (!a.startAt) return 1;
            if (!b.startAt) return -1;
            return String(a.startAt).localeCompare(String(b.startAt));
        }
      });
  }, [active, dayKey, cities, areaMode, brokerage, villageOnly, minPrice, maxPrice, sqftMin, sqftMax, bedsMin, sortBy, search]);

  // ── Refresh (manual + auto once on mount when empty/stale) ──
  const handleRefresh = () => {
    refreshMut.mutate(undefined, {
      onSuccess: (res) => toast(refreshMessage(res), 'success'),
      onError: (e) => toast(`Refresh failed: ${e.message}`, 'error'),
    });
  };

  const autoRefreshed = useRef(false);
  useEffect(() => {
    if (autoRefreshed.current || isLoading) return;
    autoRefreshed.current = true;
    const staleMs = lastScrape ? Date.now() - new Date(lastScrape).getTime() : Infinity;
    if (scrapedCount === 0 || staleMs > 24 * 3600 * 1000) {
      refreshMut.mutate(undefined, {
        onSuccess: (res) => toast(refreshMessage(res), 'success'),
        onError: (e) => console.warn('[OpenHouse] auto-refresh failed:', e.message),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, scrapedCount, lastScrape]);

  // ── Lazy geocoding of filtered rows missing coords (≤10 per mount) ──
  const geocodeBudget = useRef(10);
  const geocodeBusy = useRef(false);
  useEffect(() => {
    if (!mapsReady || geocodeBusy.current || geocodeBudget.current <= 0) return;
    const targets = filtered.filter((oh) => (oh.lat == null || oh.lng == null) && oh.address);
    if (targets.length === 0) return;
    geocodeBusy.current = true;
    (async () => {
      for (const oh of targets.slice(0, geocodeBudget.current)) {
        try {
          const full = [oh.address, oh.city, 'CA'].filter(Boolean).join(', ');
          const { lat, lng } = await geocodeAddress(full);
          await updateOh.mutateAsync({ id: oh.id, lat, lng });
        } catch { /* silent per-row */ }
        geocodeBudget.current -= 1;
      }
      geocodeBusy.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsReady, filtered]);

  // ── Map markers ──
  const markers = useMemo(
    () => filtered
      .filter((oh) => oh.lat != null && oh.lng != null)
      .map((oh) => ({
        id: oh.id,
        lat: oh.lat,
        lng: oh.lng,
        label: pinLabel(oh.price),
        color: isVillage(oh.brokerage) ? VILLAGE_COLOR : '#3B82F6',
        title: oh.address,
        subtitle: [fmtWindow(oh.startAt, oh.endAt), oh.agentName, oh.brokerage].filter(Boolean).join(' · '),
        url: oh.listingUrl,
      })),
    [filtered],
  );

  const handleMarkerClick = (id) => {
    setFocusId(id);
    cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleArchive = (oh) => {
    updateOh.mutate({ id: oh.id, status: 'archived' }, {
      onSuccess: () => toast('Archived', 'info'),
      onError: (e) => toast(`Archive failed: ${e.message}`, 'error'),
    });
    setSelectedIds((prev) => {
      if (!prev.has(oh.id)) return prev;
      const next = new Set(prev);
      next.delete(oh.id);
      return next;
    });
  };

  const selectedHouses = useMemo(
    () => active.filter((oh) => selectedIds.has(oh.id)),
    [active, selectedIds],
  );

  const mapEl = (
    <OpenHouseMap
      markers={markers}
      selectedId={focusId}
      onMarkerClick={handleMarkerClick}
      height={isMobile ? 320 : 560}
    />
  );

  const listEl = (
    <Stack spacing={1}>
      {isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <HouseIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            {active.length === 0 ? 'No open houses yet' : 'Nothing matches these filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {active.length === 0
              ? 'Hit Refresh to scrape Redfin + Village Properties.'
              : 'Try a different day, area, or price filter.'}
          </Typography>
          {active.length === 0 && (
            <Button
              variant="contained"
              startIcon={refreshMut.isPending ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshMut.isPending}
            >
              Refresh
            </Button>
          )}
        </Paper>
      ) : (
        filtered.map((oh) => (
          <HouseCard
            key={oh.id}
            oh={oh}
            selected={selectedIds.has(oh.id)}
            focused={focusId === oh.id}
            onToggle={toggleSelect}
            onFocus={setFocusId}
            onArchive={handleArchive}
            onOpenDetail={setDetailOh}
            cardRef={(el) => { cardRefs.current[oh.id] = el; }}
          />
        ))
      )}
    </Stack>
  );

  return (
    <Box sx={{ pb: selectedIds.size > 0 ? 8 : 0 }}>
      {/* Header: freshness + refresh + overflow */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {lastScrape
            ? `Updated ${agoLabel(lastScrape)} · ${active.length} open house${active.length === 1 ? '' : 's'}`
            : `${active.length} open house${active.length === 1 ? '' : 's'}`}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {zillowRunning && (
            <Chip size="small" icon={<CircularProgress size={12} />} label="Fetching photos…" sx={{ mr: 0.5 }} />
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={refreshMut.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshMut.isPending}
          >
            Refresh
          </Button>
          {!isHidden('openhouse.browseMore') && (
            <>
              <IconButton size="small" data-focus="openhouse.browseMore" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} data-focus="openhouse.browseMore">
                <MenuItem onClick={() => { setMenuAnchor(null); handleEnrichZillow(); }} disabled={zillowRunning}>
                  <PhotoLibraryIcon fontSize="small" sx={{ mr: 1 }} /> Get Zillow photos &amp; agents
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setImportOpen(true); }}>
                  <AiIcon fontSize="small" sx={{ mr: 1 }} /> Import (AI paste)
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setManualOpen(true); }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add manually
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Stack>

      {/* Day chips */}
      {dayOptions.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
          {dayOptions.map((k) => (
            <Chip
              key={k}
              size="small"
              label={ptDayLabel(k)}
              color={dayKey === k ? 'primary' : 'default'}
              variant={dayKey === k ? 'filled' : 'outlined'}
              onClick={() => setDayKey(dayKey === k ? null : k)}
            />
          ))}
        </Stack>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          size="small"
          label={areaMode === 'south' ? 'South County' : 'All areas'}
          color={areaMode === 'south' ? 'primary' : 'default'}
          variant={areaMode === 'south' ? 'filled' : 'outlined'}
          onClick={() => setAreaMode(areaMode === 'south' ? 'all' : 'south')}
        />
        <Chip
          size="small"
          label="Village Properties"
          variant={villageOnly ? 'filled' : 'outlined'}
          onClick={() => setVillageOnly(!villageOnly)}
          sx={villageOnly
            ? { bgcolor: VILLAGE_COLOR, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#D946EF' } }
            : { borderColor: VILLAGE_COLOR, color: VILLAGE_COLOR, fontWeight: 600 }}
        />
        <Autocomplete
          multiple
          size="small"
          limitTags={2}
          options={cityOptions}
          value={cities}
          onChange={(e, v) => setCities(v)}
          renderInput={(p) => <TextField {...p} label="Cities" placeholder={cities.length ? '' : 'Any'} />}
          sx={{ minWidth: 170, flexGrow: isMobile ? 1 : 0 }}
        />
        <Autocomplete
          size="small"
          options={brokerageOptions}
          value={brokerage}
          onChange={(e, v) => setBrokerage(v)}
          isOptionEqualToValue={(opt, val) => opt === val}
          renderOption={(props, option) => (
            <li {...props} key={option} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option}</span>
              <span style={{ opacity: 0.6, flexShrink: 0 }}>{brokerageCounts.get(option) || 0}</span>
            </li>
          )}
          renderInput={(p) => <TextField {...p} label="Brokerage" />}
          sx={{ minWidth: 200, flexGrow: isMobile ? 1 : 0 }}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Min price</InputLabel>
          <Select label="Min price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)}>
            <MenuItem value={0}>Any</MenuItem>
            <MenuItem value={1000000}>$1M+</MenuItem>
            <MenuItem value={2000000}>$2M+</MenuItem>
            <MenuItem value={3000000}>$3M+</MenuItem>
            <MenuItem value={5000000}>$5M+</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Max price</InputLabel>
          <Select label="Max price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}>
            <MenuItem value={0}>Any</MenuItem>
            <MenuItem value={1000000}>$1M</MenuItem>
            <MenuItem value={2000000}>$2M</MenuItem>
            <MenuItem value={3000000}>$3M</MenuItem>
            <MenuItem value={5000000}>$5M</MenuItem>
            <MenuItem value={10000000}>$10M</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small" type="number" label="Min sqft" value={sqftMin}
          onChange={(e) => setSqftMin(e.target.value)}
          sx={{ width: 96 }} InputProps={{ inputProps: { min: 0, step: 100 } }}
        />
        <TextField
          size="small" type="number" label="Max sqft" value={sqftMax}
          onChange={(e) => setSqftMax(e.target.value)}
          sx={{ width: 96 }} InputProps={{ inputProps: { min: 0, step: 100 } }}
        />
        <FormControl size="small" sx={{ minWidth: 86 }}>
          <InputLabel>Beds</InputLabel>
          <Select label="Beds" value={bedsMin} onChange={(e) => setBedsMin(e.target.value)}>
            <MenuItem value={0}>Any</MenuItem>
            <MenuItem value={1}>1+</MenuItem>
            <MenuItem value={2}>2+</MenuItem>
            <MenuItem value={3}>3+</MenuItem>
            <MenuItem value={4}>4+</MenuItem>
            <MenuItem value={5}>5+</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Sort by</InputLabel>
          <Select label="Sort by" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="time">Time (earliest)</MenuItem>
            <MenuItem value="priceDesc">Price (high → low)</MenuItem>
            <MenuItem value="priceAsc">Price (low → high)</MenuItem>
            <MenuItem value="sqftDesc">Sqft (large → small)</MenuItem>
            <MenuItem value="sqftAsc">Sqft (small → large)</MenuItem>
            <MenuItem value="bedsDesc">Beds (most first)</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search address / agent"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, flex: 1 }}
        />
      </Stack>

      {/* Layout: desktop = list + sticky map, mobile = collapsible map + list */}
      {isMobile ? (
        <>
          <Button
            size="small"
            startIcon={<MapIcon />}
            onClick={() => setMapOpen((o) => !o)}
            sx={{ mb: 1 }}
          >
            {mapOpen ? 'Hide map' : 'Show map'}
          </Button>
          <Collapse in={mapOpen} unmountOnExit>
            <Box sx={{ mb: 1.5 }}>{mapEl}</Box>
          </Collapse>
          {listEl}
        </>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ width: '55%' }}>{listEl}</Box>
          <Box sx={{ width: '45%', position: 'sticky', top: 16 }}>{mapEl}</Box>
        </Box>
      )}

      {/* Selection bottom bar */}
      {selectedIds.size > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 84 : 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1200,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: 3,
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} selected</Typography>
          <Divider orientation="vertical" flexItem />
          <Button size="small" color="inherit" onClick={() => setSelectedIds(new Set())}>Clear</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setPlanDialogOpen(true)}
          >
            Add to plan
          </Button>
        </Paper>
      )}

      <AddToPlanDialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        houses={selectedHouses}
        dayKey={dayKey}
        selectedPlanId={selectedPlanId}
        onDone={(planId) => {
          setPlanDialogOpen(false);
          setSelectedIds(new Set());
          onOpenPlan?.(planId);
        }}
      />
      <ImportPasteDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <ManualAddDialog open={manualOpen} onClose={() => setManualOpen(false)} />
      <OpenHouseDetailModal
        oh={detailOh}
        open={!!detailOh}
        onClose={() => setDetailOh(null)}
        onArchive={handleArchive}
        inPlan={detailOh ? selectedIds.has(detailOh.id) : false}
        onAddToPlan={(oh) => {
          if (!selectedIds.has(oh.id)) toggleSelect(oh.id);
          setDetailOh(null);
          toast('Added to selection — hit "Add to plan" at the bottom', 'info');
        }}
      />
    </Box>
  );
}
